import { useState } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  getListOrganizationWebhooksQueryKey,
  getListOrganizationWebhooksQueryOptions,
  useDeleteOrganizationWebhook,
  useUpdateOrganizationWebhook,
  type OrganizationWebhookDto,
} from '@sara/api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@sara/ui/components/alert-dialog';
import { buttonVariants } from '@sara/ui/components/button';
import { Dialog } from '@sara/ui/components/dialog';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { WebhookList } from '../../components/WebhookList';
import { EditWebhookDialog } from '../EditWebhookDialog';
import { toSuspense } from '../../../../lib/query';
import type { WebhookListContainerProps } from './types';

export function WebhookListContainerContent({ orgId, onCreate }: WebhookListContainerProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<OrganizationWebhookDto | null>(null);
  const [deleting, setDeleting] = useState<OrganizationWebhookDto | null>(null);
  const [togglePending, setTogglePending] = useState<ReadonlySet<string>>(new Set());

  const { data: webhooks } = useSuspenseQuery(
    toSuspense(getListOrganizationWebhooksQueryOptions({ orgId }))
  );

  const { mutate: update } = useUpdateOrganizationWebhook({
    mutation: {
      onSettled: (_data, _err, variables) => {
        setTogglePending((prev) => {
          const next = new Set(prev);
          next.delete(variables.id);
          return next;
        });
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationWebhooksQueryKey({ orgId }),
        });
      },
      onError: (err) => {
        toast.error('Failed to update webhook', {
          description: err instanceof Error ? err.message : undefined,
        });
      },
    },
  });

  const { mutate: del, isPending: isDeleting } = useDeleteOrganizationWebhook({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationWebhooksQueryKey({ orgId }),
        });
        const deleted = webhooks.find((w) => w.id === variables.id);
        setDeleting(null);
        toast.success(`Deleted ${deleted?.label ?? 'webhook'}`);
      },
      onError: (err) => {
        toast.error('Failed to delete webhook', {
          description: err instanceof Error ? err.message : undefined,
        });
      },
    },
  });

  function handleToggleEnabled(webhook: OrganizationWebhookDto, nextEnabled: boolean) {
    setTogglePending((prev) => {
      const next = new Set(prev);
      next.add(webhook.id);
      return next;
    });
    update({ id: webhook.id, data: { enabled: nextEnabled } });
  }

  return (
    <>
      <WebhookList
        webhooks={webhooks}
        onToggleEnabled={handleToggleEnabled}
        onEdit={setEditing}
        onDelete={setDeleting}
        pendingToggleIds={togglePending}
        onCreate={onCreate}
      />

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        {editing && (
          <EditWebhookDialog orgId={orgId} webhook={editing} onClose={() => setEditing(null)} />
        )}
      </Dialog>

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting ? (
                <>
                  Future event deliveries to{' '}
                  <span className="font-medium text-foreground">
                    {deleting.label ?? deleting.url}
                  </span>{' '}
                  will stop immediately. This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(buttonVariants({ variant: 'outline', shape: 'pill', size: 'sm' }))}
              disabled={isDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive', shape: 'pill', size: 'sm' }))}
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                if (!deleting) return;
                del({ id: deleting.id });
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete webhook'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
